// Feature access guard component

import { ReactNode } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Button, HStack, Text, VStack } from '@chakra-ui/react';
import { useAuth } from '@/lib/sdk/auth/hooks';
import { helpers } from '@/client/utils/helpers';
import { FeatureAccess } from '@/lib/sdk/organization/types';
import { FeaturePermissions } from '@/lib/sdk/auth/types';
import { NoFeatureAccess } from './NoFeatureAccess';
import { featurePath, firstFeaturePath } from '../featurePaths';

interface FeatureGuardProps {
  children: ReactNode;
  feature: FeatureAccess;
  action?: string;
}

export function FeatureGuard({ children, feature, action }: FeatureGuardProps) {
  const { currentOrg, user, userPermission, featurePermissions, permissionsError, isLoading, setCurrentOrg } = useAuth();
  const navigate = useNavigate();
  const isSuperAdmin = user?.is_super_admin ?? false;

  // Without permissions the checks below can't run; offer a retry rather than a blank page.
  if (permissionsError && currentOrg && !isSuperAdmin) {
    return (
      <VStack data-testid="feature-guard-error" spacing={4} py={16} textAlign="center">
        <Text fontSize="xl" fontWeight="semibold">Couldn't load your permissions for {currentOrg.name}</Text>
        <Text color="gray.600">Check your connection and try again.</Text>
        <HStack spacing={3}>
          <Button colorScheme="brand" data-testid="feature-guard-retry" onClick={() => { setCurrentOrg(currentOrg).catch(() => undefined); }}>
            Try again
          </Button>
          <Button variant="outline" data-testid="feature-guard-choose-org" onClick={() => navigate('/client/orgs')}>
            Choose another organization
          </Button>
        </HStack>
      </VStack>
    );
  }

  // Wait for auth to resolve before redirecting — prevents flash on direct URL access.
  if (isLoading || (user && featurePermissions === null && !isSuperAdmin)) {
    return null;
  }

  if (!currentOrg) {
    return <Navigate to="/client/orgs" replace />;
  }

  if (isSuperAdmin) {
    return <>{children}</>;
  }

  // Check org-level feature access
  const orgHasFeature = helpers.hasFeatureAccess(currentOrg.feature_access, feature);
  if (!orgHasFeature) {
    const orgFeaturePath = firstFeaturePath(currentOrg.feature_access || []);
    if (!orgFeaturePath) {
      return <NoFeatureAccess orgName={currentOrg.name} reason="org" />;
    }
    return <Navigate to={`/dashboard/${currentOrg.id}/${orgFeaturePath}`} replace />;
  }

  // Check user-level feature access — skip when permissions haven't loaded yet
  if (featurePermissions !== null) {
    const userHasFeature = helpers.hasFeature(
      featurePermissions,
      userPermission,
      isSuperAdmin,
      feature as keyof FeaturePermissions
    );
    if (!userHasFeature) {
      // Find first feature the user actually has access to
      const userFeatures = Object.keys(featurePermissions).filter(
        (f) => helpers.hasFeature(featurePermissions, userPermission, isSuperAdmin, f as keyof FeaturePermissions)
      );
      const userFeaturePath = firstFeaturePath(userFeatures);
      if (!userFeaturePath) {
        return <NoFeatureAccess orgName={currentOrg.name} reason="user" />;
      }
      return <Navigate to={`/dashboard/${currentOrg.id}/${userFeaturePath}`} replace />;
    }
  }

  // Check action-level permission if specified
  if (action && !helpers.hasFeatureAction(featurePermissions, userPermission, isSuperAdmin, feature, action)) {
    // A feature without a page falls back to the dashboard index, which picks one.
    return <Navigate to={`/dashboard/${currentOrg.id}/${featurePath(feature) ?? ''}`} replace />;
  }

  return <>{children}</>;
}
